const cognitoRegion = import.meta.env.VITE_COGNITO_REGION || 'eu-west-1'
const ENDPOINT = `https://cognito-idp.${cognitoRegion}.amazonaws.com/`

function base64UrlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const binary = atob(base64 + padding)
  return Uint8Array.from(binary, (c) => c.charCodeAt(0))
}

function bufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  bytes.forEach((b) => { binary += String.fromCharCode(b) })
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

async function cognitoPost(target, body) {
  const resp = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  })
  const data = await resp.json()
  if (!resp.ok) {
    throw new Error(data.message || data.__type || `${target} failed`)
  }
  return data
}

/**
 * List registered passkeys for the current user.
 * @param {string} accessToken - Cognito access token
 * @returns {Promise<{credentials: Array, count: number}>}
 */
export async function listPasskeys(accessToken) {
  const data = await cognitoPost('ListWebAuthnCredentials', {
    AccessToken: accessToken,
  })
  const credentials = data.Credentials || []
  return { credentials, count: credentials.length }
}

/**
 * Register a new passkey via the Cognito WebAuthn API + browser WebAuthn API.
 * @param {string} accessToken - Cognito access token
 * @returns {Promise<void>}
 */
export async function registerPasskey(accessToken) {
  // Step 1: Get credential creation options from Cognito
  const startData = await cognitoPost('StartWebAuthnRegistration', {
    AccessToken: accessToken,
  })

  // Cognito returns CredentialCreationOptions which may contain a publicKey wrapper
  const options = startData.CredentialCreationOptions || startData
  const pk = options.publicKey || options

  // Step 2: Convert base64url fields to ArrayBuffers for the browser WebAuthn API
  const publicKey = {
    ...pk,
    challenge: base64UrlToBuffer(pk.challenge),
    user: {
      ...pk.user,
      id: base64UrlToBuffer(pk.user.id),
    },
    excludeCredentials: (pk.excludeCredentials || []).map((c) => ({
      ...c,
      id: base64UrlToBuffer(c.id),
    })),
  }

  // Step 3: Create credential via browser WebAuthn API
  const credential = await navigator.credentials.create({ publicKey })

  // Step 4: Build the credential response for Cognito
  // Use PublicKeyCredential.toJSON() if available (modern browsers), otherwise manual build
  let credentialData
  if (typeof credential.toJSON === 'function') {
    credentialData = credential.toJSON()
  } else {
    credentialData = {
      id: credential.id,
      type: credential.type,
      rawId: bufferToBase64Url(credential.rawId),
      response: {
        clientDataJSON: bufferToBase64Url(credential.response.clientDataJSON),
        attestationObject: bufferToBase64Url(credential.response.attestationObject),
      },
      authenticatorAttachment: credential.authenticatorAttachment || undefined,
      clientExtensionResults: credential.getClientExtensionResults() || {},
    }
    if (typeof credential.response.getTransports === 'function') {
      credentialData.response.transports = credential.response.getTransports()
    }
  }

  // Step 5: Complete registration
  await cognitoPost('CompleteWebAuthnRegistration', {
    AccessToken: accessToken,
    Credential: credentialData,
  })
}
